/**
 * SkillFormDialog — F-SKILL-1 Packet B1 + B2
 *
 * Combined create + edit sheet for custom skills.
 *
 * B1: AC-SKILL-04, 05 — create (name/displayName/description/category/content + .md import)
 * B2: AC-SKILL-08 (F) — attach reference files + status
 *     AC-SKILL-11 (F) — edit (display name, description, category, content, active)
 *     AC-SKILL-18 (F) — version history list + read-only preview
 *     AC-SKILL-19 (F) — restore prior version
 *
 * Fold-in (MEDIUM): Description is required server-side → add client validation + "required" marker.
 */

import React, { useRef, useState } from 'react';
import { Upload, Trash2, FileText, Clock, RotateCcw, Loader2 } from 'lucide-react';
import { t } from '@/lib/i18n';
import { FormSheet } from '@/components/ui/FormSheet';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { Alert } from '@/components/ui/Alert';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { MdFileInput } from '@/components/ui/MdFileInput';
import { FileInput } from '@/components/ui/FileInput';
import { Switch } from '@/components/ui/Switch';
import { showToast } from '@/components/ui/Toast';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  useRorosSkillCreate,
  useRorosSkillUpdateB2,
  useSkillAttachments,
  useSkillUploadAttachment,
  useSkillRemoveAttachment,
  useSkillVersions,
  useSkillVersion,
  useSkillRestore,
} from '@/hooks/roros/skills';
import type {
  SkillCategory,
  RorosSkillDTO,
  RorosSkillAttachmentDTO,
} from '@/types/roros/skills';
import type { UpdateSkillPayload } from '@/services/roros/skills.service';
import { formatDateTimeStr } from '@/lib/localeFormatters';

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_OPTIONS = [
  { value: 'domain', label: t('Domain') },
  { value: 'infrastructure', label: t('Infrastructure') },
  { value: 'tool', label: t('Tool') },
  { value: 'integration', label: t('Integration') },
  { value: 'quality', label: t('Quality') },
  { value: 'content', label: t('Content') },
];

// Allowed MIME types for reference file upload (mirrors server ALLOWED_SKILL_REF_MIME_TYPES)
const ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
]);

// Max size per backend: 100 MB
const MAX_FILE_BYTES = 100 * 1024 * 1024;

// ─── Snapshot type ────────────────────────────────────────────────────────────

type SkillSnapshot = {
  name: string;
  displayName: string;
  description: string;
  category: SkillCategory;
  content: string;
  isActive: boolean;
};

const DEFAULT_SNAPSHOT: SkillSnapshot = {
  name: '',
  displayName: '',
  description: '',
  category: 'domain',
  content: '',
  isActive: true,
};

// ─── Field errors ─────────────────────────────────────────────────────────────

interface FieldErrors {
  name?: string;
  displayName?: string;
  description?: string;
  content?: string;
  importFile?: string;
  general?: string;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
  /** When provided the dialog opens in edit mode for the given skill. */
  skill?: RorosSkillDTO | null;
  /** Whether the current user has _MANAGE permission */
  canManage?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}



function AttachmentStatusBadge({ status }: { status: RorosSkillAttachmentDTO['status'] }) {
  if (status === 'ready') {
    return <Badge variant="success" data-testid="attachment-status-ready">{t('Ready')}</Badge>;
  }
  if (status === 'processing') {
    return <Badge variant="secondary" data-testid="attachment-status-processing">{t('Processing')}</Badge>;
  }
  return <Badge variant="error" data-testid="attachment-status-failed">{t('Failed')}</Badge>;
}

// ─── Attachments panel (AC-SKILL-08) ─────────────────────────────────────────

function AttachmentsPanel({
  skillId,
  canManage,
}: {
  skillId: string;
  canManage: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | undefined>();
  const [removingId, setRemovingId] = useState<string | null>(null);

  const attachmentsQuery = useSkillAttachments(skillId);
  const uploadMutation = useSkillUploadAttachment();
  const removeMutation = useSkillRemoveAttachment();

  const attachments = attachmentsQuery.data ?? [];

  function handleUploadClick() {
    setFileError(undefined);
    fileInputRef.current?.click();
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    // Client-side MIME check (by extension when MIME unavailable)
    const hasMime = file.type && file.type !== '';
    const mimeOk = hasMime ? ALLOWED_MIME_TYPES.has(file.type) : true;
    const nameOk = /\.(pdf|doc|docx|txt|md)$/i.test(file.name);
    if (!mimeOk || !nameOk) {
      setFileError(t('File type not allowed. Supported: PDF, Word, plain text, Markdown'));
      return;
    }

    // Size check
    if (file.size > MAX_FILE_BYTES) {
      setFileError(t('File exceeds 100 MB limit'));
      return;
    }

    setFileError(undefined);
    try {
      await uploadMutation.mutateAsync({ skillId, file });
      showToast.success(t('Reference file attached'));
    } catch (err: unknown) {
      const e = err as { status?: number; message?: string; code?: string };
      if (e.status === 422 || e.code === 'MIME_NOT_ALLOWED') {
        setFileError(t('File type not allowed by server'));
      } else if (e.status === 413 || e.code === 'FILE_TOO_LARGE') {
        setFileError(t('File exceeds 100 MB limit'));
      } else {
        setFileError(e.message ?? t('Upload failed'));
      }
    }
  }

  async function handleRemove(linkId: string) {
    setRemovingId(linkId);
    try {
      await removeMutation.mutateAsync({ skillId, linkId });
      showToast.success(t('Reference file removed'));
    } catch {
      showToast.error(t('Failed to remove file'));
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <div className="space-y-4" data-testid="attachments-panel">
      {/* Upload control */}
      {canManage && (
        <div>
          <FileInput
            inputRef={fileInputRef}
            accept=".pdf,.doc,.docx,.txt,.md"
            onChange={handleFileChange}
            data-testid="skill-attach-file-input"
            aria-label={t('Attach reference file')}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleUploadClick}
            disabled={uploadMutation.isPending}
            data-testid="skill-attach-button"
          >
            {uploadMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <Upload className="w-3.5 h-3.5 mr-1.5" />
            )}
            {t('Attach file')}
          </Button>
          {fileError && (
            <p className="text-sm text-error-600 mt-1" data-testid="skill-attach-error">
              {fileError}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            {t('Supported: PDF, Word, plain text, Markdown (max 100 MB)')}
          </p>
        </div>
      )}

      {/* Loading state */}
      {attachmentsQuery.isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('Loading…')}
        </div>
      )}

      {/* Empty state */}
      {!attachmentsQuery.isLoading && attachments.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="attachments-empty">
          {t('No reference files attached')}
        </p>
      )}

      {/* Attachment list */}
      {attachments.length > 0 && (
        <div className="divide-y divide-border rounded-md border border-border" data-testid="attachments-list">
          {attachments.map((att) => (
            <div
              key={att.id}
              className="flex items-center gap-3 px-3 py-2.5"
              data-testid="attachment-row"
            >
              <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate" data-testid="attachment-filename">
                  {att.filename}
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatFileSize(att.size)}
                </div>
              </div>
              <div className="shrink-0" data-testid="attachment-status">
                <AttachmentStatusBadge status={att.status} />
              </div>
              {canManage && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemove(att.id)}
                  disabled={removingId === att.id}
                  aria-label={t('Remove attachment')}
                  data-testid="attachment-remove-button"
                >
                  {removingId === att.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  )}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Version history panel (AC-SKILL-18 + 19) ────────────────────────────────

function VersionHistoryPanel({
  skillId,
  canManage,
  onRestored,
}: {
  skillId: string;
  canManage: boolean;
  onRestored: () => void;
}) {
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [restoreConfirmVersionId, setRestoreConfirmVersionId] = useState<string | null>(null);

  const versionsQuery = useSkillVersions(skillId);
  const versionDetailQuery = useSkillVersion(skillId, selectedVersionId);
  const restoreMutation = useSkillRestore();

  const versions = versionsQuery.data ?? [];
  const selectedVersion = versionDetailQuery.data;

  const sortedVersions = [...versions].sort((a, b) => b.revisionNumber - a.revisionNumber);

  async function handleRestore(versionId: string) {
    try {
      await restoreMutation.mutateAsync({ skillId, versionId });
      showToast.success(t('Version restored'));
      setRestoreConfirmVersionId(null);
      setSelectedVersionId(null);
      onRestored();
    } catch {
      showToast.error(t('Failed to restore version'));
    }
  }

  return (
    <div className="space-y-4" data-testid="version-history-panel">
      {versionsQuery.isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('Loading…')}
        </div>
      )}

      {!versionsQuery.isLoading && versions.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="versions-empty">
          {t('No version history')}
        </p>
      )}

      {/* Version list (newest first) */}
      {sortedVersions.length > 0 && (
        <div className="divide-y divide-border rounded-md border border-border" data-testid="versions-list">
          {sortedVersions.map((v) => (
            <div
              key={v.id}
              className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/20 transition-colors ${selectedVersionId === v.id ? 'bg-muted/30' : ''}`}
              onClick={() => setSelectedVersionId(selectedVersionId === v.id ? null : v.id)}
              data-testid="version-row"
            >
              <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium" data-testid="version-revision">
                  {t('Revision')} {v.revisionNumber}
                </div>
                <div className="text-xs text-muted-foreground" data-testid="version-timestamp">
                  {formatDateTimeStr(v.createdAt)}
                </div>
              </div>
              {canManage && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRestoreConfirmVersionId(v.id);
                  }}
                  data-testid="version-restore-button"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />
                  {t('Restore')}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Read-only snapshot preview */}
      {selectedVersionId && !versionsQuery.isLoading && (
        <div className="mt-3">
          {versionDetailQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('Loading…')}
            </div>
          ) : selectedVersion ? (
            <div className="rounded-md border border-border bg-muted/20 p-3" data-testid="version-preview">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                {t('Snapshot preview (read-only)')}
              </p>
              <Textarea
                value={selectedVersion.content}
                readOnly
                rows={10}
                className="font-mono text-xs bg-muted/40"
                data-testid="version-preview-content"
              />
            </div>
          ) : null}
        </div>
      )}

      {/* Restore confirm dialog */}
      <ConfirmDialog
        isOpen={!!restoreConfirmVersionId}
        onClose={() => setRestoreConfirmVersionId(null)}
        onConfirm={() => restoreConfirmVersionId && handleRestore(restoreConfirmVersionId)}
        title={t('Restore this version?')}
        description={t('Current content and reference files will be replaced. A new history entry will record the restore.')}
        confirmText={t('Restore')}
        variant="primary"
        isLoading={restoreMutation.isPending}
        confirmTestId="version-restore-confirm-button"
        cancelTestId="version-restore-cancel-button"
      />
    </div>
  );
}

// ─── Details tab content ──────────────────────────────────────────────────────

function SkillDetailsTab({
  isEdit,
  state,
  setField,
  errors,
  onImportClick,
  fileInputRef,
}: {
  isEdit: boolean;
  state: SkillSnapshot;
  setField: (key: keyof SkillSnapshot, value: string | boolean) => void;
  errors: FieldErrors;
  onImportClick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
}) {
  return (
    <div className="space-y-4">
      {errors.general && (
        <Alert variant="error" data-testid="skill-form-error">
          {errors.general}
        </Alert>
      )}

      {/* Name (create-only) */}
      {!isEdit && (
        <Input
          label={t('Name (unique identifier)')}
          value={state.name}
          onChange={(e) => setField('name', e.target.value)}
          placeholder={t('e.g. sprint-review-facilitator')}
          error={errors.name}
          data-testid="skill-name-input"
        />
      )}

      {/* Display name */}
      <Input
        label={`${t('Display name')} *`}
        value={state.displayName}
        onChange={(e) => setField('displayName', e.target.value)}
        placeholder={t('e.g. Sprint Review Facilitator')}
        error={errors.displayName}
        data-testid="skill-display-name-input"
      />

      {/* Description — required (FOLD-IN fix) */}
      <Textarea
        label={`${t('Description')} *`}
        value={state.description}
        onChange={(e) => setField('description', e.target.value)}
        placeholder={t('Brief description of what this skill enables')}
        rows={2}
        error={errors.description}
        data-testid="skill-description-input"
      />

      {/* Category */}
      <Select
        label={t('Category')}
        value={state.category}
        onValueChange={(v) => setField('category', v as SkillCategory)}
        options={CATEGORY_OPTIONS}
        data-testid="skill-category-select"
      />

      {/* Active toggle (edit only) */}
      {isEdit && (
        <Switch
          label={t('Active')}
          checked={state.isActive}
          onChange={(e) => setField('isActive', e.target.checked)}
          data-testid="skill-active-toggle"
        />
      )}

      {/* Markdown content */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="block text-sm font-medium text-foreground">
            {t('Content (Markdown)')} *
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onImportClick}
            data-testid="skill-import-button"
          >
            <Upload className="w-3.5 h-3.5 mr-1" />
            {t('Import .md')}
          </Button>
        </div>
        {errors.importFile && (
          <p className="text-sm text-error-600 mb-1" data-testid="skill-import-error">
            {errors.importFile}
          </p>
        )}
        <Textarea
          value={state.content}
          onChange={(e) => setField('content', e.target.value)}
          placeholder={t('# Skill instructions\n\nDescribe what the operator should do…')}
          rows={14}
          className="font-mono text-xs"
          error={errors.content}
          data-testid="skill-content-input"
        />
      </div>
    </div>
  );
}

// ─── Diff helper ──────────────────────────────────────────────────────────────

function diffSnapshots(
  base: SkillSnapshot,
  cur: SkillSnapshot,
): Partial<Record<keyof SkillSnapshot, boolean>> {
  return {
    name: base.name !== cur.name,
    displayName: base.displayName !== cur.displayName,
    description: base.description !== cur.description,
    category: base.category !== cur.category,
    content: base.content !== cur.content,
    isActive: base.isActive !== cur.isActive,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SkillFormDialog({ isOpen, onClose, onSaved, skill, canManage = true }: Props) {
  const isEdit = !!skill;
  const isSystemSkill = isEdit && (skill?.isSystem ?? false);
  // System skills: read-only (no edits, no attach, no restore)
  const editAllowed = !isSystemSkill && canManage;

  const [errors, setErrors] = useState<FieldErrors>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const createMutation = useRorosSkillCreate();
  const updateMutation = useRorosSkillUpdateB2();
  const isPending = isEdit ? updateMutation.isPending : createMutation.isPending;

  const initialSnapshot = isEdit && skill
    ? {
        name: skill.name,
        displayName: skill.displayName,
        description: skill.description,
        category: skill.category as SkillCategory,
        content: skill.content,
        isActive: skill.isActive,
      }
    : DEFAULT_SNAPSHOT;

  const { state, setField, baseline, current, reset } = useFormSnapshot<SkillSnapshot>({
    initialFactory: () => initialSnapshot,
    isOpen,
    depKey: skill?.id ?? null,
  });

  function handleClose() {
    reset();
    setErrors({});
    onClose();
  }

  function handleSavedAndClose() {
    reset();
    setErrors({});
    onSaved?.();
    onClose();
  }

  // ─── .md import ────────────────────────────────────────────────────────────

  function handleImportClick() {
    fileInputRef.current?.click();
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!fileInputRef.current) return;
    fileInputRef.current.value = '';
    if (!file) return;

    if (!file.name.endsWith('.md')) {
      setErrors((prev) => ({ ...prev, importFile: t('Only .md files can be imported') }));
      return;
    }

    try {
      const text = await file.text();
      setField('content', text);
      setErrors((prev) => ({ ...prev, importFile: undefined }));
    } catch {
      setErrors((prev) => ({ ...prev, importFile: t('Failed to read file') }));
    }
  }

  // ─── Validation ────────────────────────────────────────────────────────────

  function validate(): FieldErrors {
    const next: FieldErrors = {};

    if (!isEdit) {
      const name = state.name.trim();
      if (!name) next.name = t('Name is required');
      else if (!/^[a-z0-9-]+$/.test(name))
        next.name = t('Name must be lowercase letters, numbers, and hyphens only');
    }

    if (!state.displayName.trim()) next.displayName = t('Display name is required');
    // FOLD-IN: description is required server-side — validate client-side too
    if (!state.description.trim()) next.description = t('Description is required');
    if (!state.content.trim()) next.content = t('Content is required');

    return next;
  }

  // ─── Submit ────────────────────────────────────────────────────────────────

  async function handleSubmit() {
    const next = validate();
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});

    try {
      if (isEdit && skill) {
        const payload: UpdateSkillPayload = {
          display_name: state.displayName.trim(),
          description: state.description.trim(),
          content: state.content,
          category: state.category,
          is_active: state.isActive,
        };
        await updateMutation.mutateAsync({ id: skill.id, payload });
        showToast.success(t('Skill updated'));
        handleSavedAndClose();
      } else {
        await createMutation.mutateAsync({
          name: state.name.trim(),
          display_name: state.displayName.trim(),
          description: state.description.trim(),
          content: state.content,
          category: state.category,
        });
        showToast.success(t('Skill created'));
        handleSavedAndClose();
      }
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      if (apiErr?.status === 409) {
        setErrors({ name: t('A skill with this name already exists in your organisation') });
      } else {
        setErrors({
          general: apiErr?.message ?? (isEdit ? t('Failed to update skill') : t('Failed to create skill')),
        });
      }
    }
  }

  // ─── Tab definitions (edit mode only) ─────────────────────────────────────

  const detailsTabContent = (
    <SkillDetailsTab
      isEdit={isEdit}
      state={state}
      setField={setField as (k: keyof SkillSnapshot, v: string | boolean) => void}
      errors={errors}
      onImportClick={handleImportClick}
      fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
    />
  );

  const tabs = isEdit && skill
    ? [
        {
          key: 'details',
          label: t('Details'),
          fields: ['displayName', 'description', 'category', 'content', 'isActive'] as Array<keyof SkillSnapshot>,
          content: detailsTabContent,
          testId: 'skill-tab-details',
        },
        {
          key: 'attachments',
          label: t('Reference files'),
          content: (
            <AttachmentsPanel
              skillId={skill.id}
              canManage={editAllowed}
            />
          ),
          testId: 'skill-tab-attachments',
        },
        {
          key: 'history',
          label: t('Version history'),
          content: (
            <VersionHistoryPanel
              skillId={skill.id}
              canManage={editAllowed}
              onRestored={handleSavedAndClose}
            />
          ),
          testId: 'skill-tab-history',
        },
      ]
    : undefined;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Hidden file input for .md import */}
      <MdFileInput
        inputRef={fileInputRef}
        onChange={handleFileChange}
        data-testid="skill-import-file-input"
      />

      <FormSheet<SkillSnapshot>
        isOpen={isOpen}
        onClose={handleClose}
        title={isEdit ? (skill?.displayName ?? t('Edit Skill')) : t('New Skill')}
        subtitle={isEdit && isSystemSkill ? t('System skills are read-only') : undefined}
        mode={isEdit ? 'edit' : 'create'}
        onSubmit={handleSubmit}
        isSubmitting={isPending}
        submitDisabled={isSystemSkill}
        baseline={baseline}
        current={current}
        diff={diffSnapshots}
        width="md"
        submitLabel={isEdit ? t('Save changes') : t('Create Skill')}
        submitTestId="skill-form-submit"
        tabs={tabs}
      >
        {/* Children shown only in create mode (no tabs) */}
        {!isEdit && (
          <SkillDetailsTab
            isEdit={false}
            state={state}
            setField={setField as (k: keyof SkillSnapshot, v: string | boolean) => void}
            errors={errors}
            onImportClick={handleImportClick}
            fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
          />
        )}
      </FormSheet>
    </>
  );
}
